<template>
  <div class="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <ValidationObserver v-if="!ssoMode" v-slot="{ handleSubmit }">
          <form
            v-if="!needsMfa"
            @submit.prevent="handleSubmit(submit)"
            class="space-y-6"
            data-testid="login-form"
          >
            <div>
              <label
                for="email"
                class="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <div class="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  v-model="email"
                  data-testid="login-email"
                  class="
                    appearance-none
                    block
                    w-full
                    px-3
                    py-2
                    border border-gray-300
                    rounded-md
                    shadow-sm
                    placeholder-gray-400
                    focus:outline-none
                    focus:ring-indigo-500
                    focus:border-indigo-500
                    sm:text-sm
                  "
                />
              </div>
            </div>

            <div>
              <label
                for="password"
                class="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div class="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required
                  v-model="password"
                  data-testid="login-password"
                  class="
                    appearance-none
                    block
                    w-full
                    px-3
                    py-2
                    border border-gray-300
                    rounded-md
                    shadow-sm
                    placeholder-gray-400
                    focus:outline-none
                    focus:ring-indigo-500
                    focus:border-indigo-500
                    sm:text-sm
                  "
                />
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  class="
                    h-4
                    w-4
                    text-indigo-600
                    focus:ring-indigo-500
                    border-gray-300
                    rounded
                  "
                />
                <label
                  for="remember-me"
                  class="ml-2 block text-sm text-gray-900"
                >
                  Remember me
                </label>
              </div>

              <div class="text-sm">
                <a
                  href="#"
                  class="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot your password?
                </a>
              </div>
            </div>
            <div>
              <p v-if="error" class="text-red-500 text-md italic">{{ error }}</p>
            </div>
            <div>
              <button
                type="submit"
                data-testid="login-submit"
                class="
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border border-transparent
                  rounded-md
                  shadow-sm
                  text-sm
                  font-medium
                  text-white
                  bg-indigo-600
                  hover:bg-indigo-700
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-indigo-500
                "
                :disabled="submitting"
                :class="{ 'opacity-50 cursor-not-allowed': submitting }"
              >
                Sign in
              </button>
            </div>
          </form>

          <form
            v-else
            @submit.prevent="handleSubmit(verifyMfa)"
            class="space-y-6"
          >
            <div>
              <p class="text-sm text-gray-700">
                A verification code has been sent to
                <span class="font-medium">{{ email }}</span>.
              </p>
              <p v-if="formattedExpiry" class="text-xs text-gray-500 mt-1">
                The code expires at {{ formattedExpiry }}.
              </p>
            </div>
            <div>
              <label
                for="mfa-code"
                class="block text-sm font-medium text-gray-700"
              >
                Verification code
              </label>
              <div class="mt-1">
                <input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputmode="numeric"
                  pattern="\d{6}"
                  maxlength="6"
                  autocomplete="one-time-code"
                  required
                  v-model="mfaCode"
                  class="
                    appearance-none
                    block
                    w-full
                    px-3
                    py-2
                    border border-gray-300
                    rounded-md
                    shadow-sm
                    placeholder-gray-400
                    focus:outline-none
                    focus:ring-indigo-500
                    focus:border-indigo-500
                    sm:text-sm
                  "
                />
              </div>
            </div>
            <div>
              <p v-if="error" class="text-red-500 text-md italic">{{ error }}</p>
            </div>
            <div class="space-y-2">
              <button
                type="submit"
                class="
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border border-transparent
                  rounded-md
                  shadow-sm
                  text-sm
                  font-medium
                  text-white
                  bg-indigo-600
                  hover:bg-indigo-700
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-indigo-500
                "
                :disabled="verifying || !mfaCode"
                :class="{ 'opacity-50 cursor-not-allowed': verifying || !mfaCode }"
              >
                Verify code
              </button>
              <button
                type="button"
                class="
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border border-gray-300
                  rounded-md
                  shadow-sm
                  text-sm
                  font-medium
                  text-gray-700
                  bg-white
                  hover:bg-gray-50
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-indigo-500
                "
                @click="restartMfa"
                :disabled="verifying"
                :class="{ 'opacity-50 cursor-not-allowed': verifying }"
              >
                Use a different account
              </button>
            </div>
          </form>
        </ValidationObserver>
        <form
          v-if="sharedSsoAvailable && ssoMode"
          class="space-y-4"
          @submit.prevent="signInWithMgc"
        >
          <h1 class="text-xl font-semibold text-gray-900">{{ connectionTest ? 'Test university sign-in' : 'Sign in with your organisation' }}</h1>
          <p v-if="connectionTest" class="text-sm text-gray-600">Continue to your university and use the pilot staff account. This checks the connection without opening an MGC account.</p>
          <p v-else class="text-sm text-gray-600">
            Enter your work email. We'll take you to your organisation's sign-in page.
          </p>
          <div v-if="!connectionTest">
            <label for="sso-email" class="block text-sm font-medium text-gray-700">Work email</label>
            <input
              id="sso-email"
              v-model.trim="ssoEmail"
              type="email"
              autocomplete="email"
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <p v-if="ssoError" class="text-sm text-red-700" role="alert">{{ ssoError }}</p>
          <button
            type="submit"
            class="w-full py-2 px-4 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            :disabled="ssoSubmitting"
          >
            Continue with SSO
          </button>
          <button
            type="button"
            class="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            :disabled="ssoSubmitting"
            v-if="!connectionTest"
            @click="signInWithStaffIdentity"
          >
            Use an internal staff account
          </button>
          <button v-if="!connectionTest" type="button" class="w-full text-sm text-indigo-700" @click="ssoMode = false">
            Back to password sign-in
          </button>
        </form>
        <button
          v-else-if="sharedSsoAvailable && !needsMfa"
          type="button"
          class="w-full mt-4 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50"
          @click="showSharedSignIn"
        >
          Sign in with your organisation
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import "../../plugins/validation";
import Auth from "../../api/auth.js";
import { sharedSsoAvailable, startSharedSignIn, connectionTestDomain } from "../../api/sharedSso.js";

export default {
  name: "Login",
  components: {},
  data: function () {
    return {
      email: null,
      password: null,
      signedIn: false,
      error: null,
      needsMfa: false,
      challengeId: null,
      mfaCode: "",
      submitting: false,
      verifying: false,
      mfaExpiresAt: null,
      ssoMode: sharedSsoAvailable() && this.$route.query.sso === "1",
      connectionTest: connectionTestDomain(this.$route.query.connection_test),
      ssoEmail: "",
      ssoError: "",
      ssoSubmitting: false,
    };
  },
  computed: {
    sharedSsoAvailable,
    formattedExpiry() {
      if (!this.mfaExpiresAt) {
        return null;
      }
      const expires = new Date(this.mfaExpiresAt);
      if (Number.isNaN(expires.getTime())) {
        return null;
      }
      return expires.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
  },
  methods: {
    showSharedSignIn() {
      this.ssoEmail = this.email || "";
      this.ssoError = "";
      this.ssoMode = true;
    },
    async signInWithMgc() {
      this.ssoError = "";
      this.ssoSubmitting = true;
      try {
        await startSharedSignIn("/admin/choose", { email: this.ssoEmail, connectionTest: this.connectionTest });
      } catch (error) {
        this.ssoError = error.message || "Unable to start sign-in. Please try again.";
        this.ssoSubmitting = false;
      }
    },
    async signInWithStaffIdentity() {
      this.ssoError = "";
      this.ssoSubmitting = true;
      try {
        await startSharedSignIn("/admin/choose", { local: true });
      } catch (error) {
        this.ssoError = "Unable to start staff sign-in. Please try again.";
        this.ssoSubmitting = false;
      }
    },
    submit() {
      this.error = null;
      this.submitting = true;
      Auth.login(
        {
          email: this.email,
          password: this.password,
        },
        true
      )
        .then((result) => {
          if (result && result.mfaRequired) {
            this.needsMfa = true;
            this.challengeId = result.challengeId;
            this.mfaExpiresAt = result.expiresAt;
            this.mfaCode = "";
            this.error = null;
          }
        })
        .catch(() => {
          this.error = "Invalid email address or password.";
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    verifyMfa() {
      if (!this.challengeId) {
        this.error = "Your verification session has expired. Please sign in again.";
        return;
      }
      const trimmedCode = (this.mfaCode || "").trim();
      if (!trimmedCode) {
        this.error = "Enter the verification code.";
        return;
      }
      this.mfaCode = trimmedCode;
      this.error = null;
      this.verifying = true;
      Auth.verifyMfa(
        {
          challenge_id: this.challengeId,
          code: trimmedCode,
        },
        true
      )
        .catch(() => {
          this.error = "Invalid or expired verification code.";
        })
        .finally(() => {
          this.verifying = false;
        });
    },
    restartMfa() {
      this.needsMfa = false;
      this.challengeId = null;
      this.mfaCode = "";
      this.mfaExpiresAt = null;
      this.error = null;
    },
  },

  created() {},
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.logo {
  max-width: 100%;
}
.text-mygrad {
  color: #08697b;
}
.bg-mygrad {
  background-color: #08697b;
}
.bg-mygrad-light {
  background-color: #05b1d2 !important;
}
.text-mygrad-light {
  color: #05b1d2;
}
</style>
